import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Spin, Tag, Typography } from 'antd';
import React from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { FormCard, FormPageContainer, FormPageHeader } from '../../components/PageLayout';
import { useBrands } from '../../hooks/useBrandQueries';
import { useCategories } from '../../hooks/useCategoryQueries';
import { useListReturn } from '../../hooks/useListReturn';
import { useProduct } from '../../hooks/useProductQueries';
import { ROUTES } from '../../routes/routes';
import { formatCategoryName } from '../../utils/categoryUtils';
import { formatPrice } from '../../utils/currency';

const { Text } = Typography;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  > * {
    min-width: 0;
  }
`;

const ImageBox = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #fff;
  border-radius: 15px;
  border: 1px solid #edf2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    max-width: 90%;
    max-height: 90%;
    object-fit: contain;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #f8fafc;
  border-radius: 15px;
  border: 1px solid #edf2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
`;

const InfoLabel = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const InfoValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 20px;
`;

const ClassificationTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 24px;
`;

const ClassTag = styled.span`
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
`;

const PriceRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 8px;
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 8px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const OldPriceValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #94a3b8;
  text-decoration: line-through;
  margin-bottom: 20px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

export const ProductViewPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { goToList, listState } = useListReturn(ROUTES.PRODUCTS);

  const { data: product, isLoading, error } = useProduct(slug || '');
  const { data: brands = [] } = useBrands();
  const { data: categories = [] } = useCategories();

  const handleBack = () => goToList();
  const handleEdit = () => {
    if (!product) return;
    navigate(generatePath(ROUTES.PRODUCT_EDIT, { slug: product.slug }), { state: listState });
  };

  if (isLoading) {
    return (
      <FormPageContainer>
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      </FormPageContainer>
    );
  }

  if (error || !product) {
    return (
      <FormPageContainer>
        <FormPageHeader>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} />
          <h2>Продукт</h2>
        </FormPageHeader>
        <Text type="danger">{error?.message || 'Продукт не знайдено'}</Text>
      </FormPageContainer>
    );
  }

  const brandName = brands.find((b) => b.id === product.brandId)?.name;
  const categoryNames = product.categoryIds
    .map((id) => formatCategoryName(categories, id))
    .filter(Boolean);

  const formattedDate = new Date(product.createdAt).toLocaleDateString('uk-UA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <FormPageContainer>
      <FormPageHeader>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} />
        <TitleRow>
          <h2 style={{ margin: 0 }}>{product.name}</h2>
          <Tag
            color={product.isActive ? 'success' : 'default'}
            style={{ borderRadius: 20, fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}
          >
            {product.isActive ? 'Активний' : 'Неактивний'}
          </Tag>
          {product.isNew && (
            <Tag
              color="blue"
              style={{
                borderRadius: 20,
                fontWeight: 700,
                fontSize: 11,
                textTransform: 'uppercase',
              }}
            >
              Новинка
            </Tag>
          )}
        </TitleRow>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={handleEdit}
          style={{ marginLeft: 'auto' }}
        >
          Редагувати
        </Button>
      </FormPageHeader>

      <ProductGrid>
        <div>
          {product.image ? (
            <ImageBox>
              <img src={product.image} alt={product.name} />
            </ImageBox>
          ) : (
            <ImagePlaceholder>Немає зображення</ImagePlaceholder>
          )}
        </div>

        <div>
          <FormCard>
            <InfoLabel>Класифікація</InfoLabel>
            <ClassificationTags>
              {brandName && <ClassTag>Бренд: {brandName}</ClassTag>}
              {categoryNames.map((name) => (
                <ClassTag key={name}>Категорія: {name}</ClassTag>
              ))}
            </ClassificationTags>

            {product.description && (
              <>
                <InfoLabel>Опис</InfoLabel>
                <div
                  style={{ lineHeight: 1.7, color: '#334155', marginBottom: 24 }}
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              </>
            )}

            <PriceRow>
              <div>
                <InfoLabel>Ціна</InfoLabel>
                <InfoValue>{formatPrice(product.price)}</InfoValue>
              </div>
              <div>
                <InfoLabel>Стара ціна</InfoLabel>
                {product.oldPrice ? (
                  <OldPriceValue>{formatPrice(product.oldPrice)}</OldPriceValue>
                ) : (
                  <InfoValue style={{ color: '#94a3b8', fontWeight: 400 }}>—</InfoValue>
                )}
              </div>
            </PriceRow>
          </FormCard>

          <FormCard>
            <StatRow>
              <div>
                <InfoLabel>Дата створення</InfoLabel>
                <InfoValue style={{ marginBottom: 0 }}>{formattedDate}</InfoValue>
              </div>
              <div>
                <InfoLabel>Перегляди</InfoLabel>
                <InfoValue style={{ marginBottom: 0 }}>{product.views}</InfoValue>
              </div>
              <div>
                <InfoLabel>Замовлення</InfoLabel>
                <InfoValue style={{ marginBottom: 0 }}>{product.ordersCount ?? 0}</InfoValue>
              </div>
            </StatRow>
          </FormCard>
        </div>
      </ProductGrid>
    </FormPageContainer>
  );
};
